import { zodResolver } from '@hookform/resolvers/zod';
import { DownloadIcon, MailIcon, MoreHorizontalIcon, UploadIcon, UserPlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { AccessLevelTiles } from '@/components/AccessLevelTiles';
import { CopyButton } from '@/components/CopyButton';
import { DataTable, type Column } from '@/components/DataTable';
import { AccessLevelCards, JobAssignmentPicker } from '@/components/JobAssignmentPicker';
import { PageIntro } from '@/components/PageIntro';
import { AccessLevelBadge, UserStatusBadge } from '@/components/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtRelative } from '@/lib/format';
import { ACCESS_LEVELS, LEVEL_DESCRIPTION, LEVEL_LABEL, highestLevel, jobRoleForLevel } from '@/lib/roles';
import type { AccessLevel, InvitationDto, JobAssignment, UserDto } from '@/lib/types';
import { cn, initials } from '@/lib/utils';
import { useInviteUser, useRevokeTenantInvitation, useSetUserJobs, useTenantInvitations, useUpdateUser, useUserDetail, useUsers } from './queries';
import { UsersImportDialog } from './UsersImportDialog';

const LEVEL_OPTIONS = ACCESS_LEVELS.map((l) => ({ value: l, label: LEVEL_LABEL[l], hint: LEVEL_DESCRIPTION[l] }));

/**
 * Everyone who can sign in to the organization. The access tiles explain the three levels and count who holds
 * each (click one to filter); the table lists people with their level, jobs and last sign-in; invitations that
 * have not been accepted sit in their own card. `?invite=1` opens the invite dialog (the masthead links here),
 * `/org/users/:userId` opens a person.
 */
export function UsersPage() {
  const me = useMe();
  const users = useUsers();
  const invitations = useTenantInvitations();
  const revoke = useRevokeTenantInvitation();
  const update = useUpdateUser();
  const [params, setParams] = useSearchParams();
  const inviteOpen = params.get('invite') === '1';
  const setInviteOpen = (open: boolean) => {
    const next = new URLSearchParams(params);
    if (open) next.set('invite', '1');
    else next.delete('invite');
    setParams(next, { replace: true });
  };
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [busy, setBusy] = useState(false);
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  // Deep link: /org/users/:userId opens that person's dialog once the list is in.
  const linked = userId ? (users.data?.items ?? []).find((u) => u.id === userId) ?? null : null;
  useEffect(() => {
    if (linked) setEditing(linked);
  }, [linked]);

  const pending = (invitations.data?.items ?? []).filter((i) => i.status === 'pending');
  const items = users.data?.items ?? [];
  const levelOf = (u: UserDto): AccessLevel => u.level ?? highestLevel(u.tenantRole, u.jobs ?? []);
  const counts = useMemo(() => {
    const c: Record<AccessLevel, number> = { super_user: 0, pm_admin: 0, pm_user: 0 };
    for (const u of items) if (u.status === 'active') c[u.level ?? highestLevel(u.tenantRole, u.jobs ?? [])] += 1;
    return c;
  }, [items]);
  const levelFilter = params.get('level');
  const pickLevel = (level: AccessLevel) => {
    const next = new URLSearchParams(params);
    if (next.get('level') === level) next.delete('level');
    else next.set('level', level);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const toggleAccess = async (u: UserDto) => {
    const status = u.status === 'active' ? 'disabled' : 'active';
    try {
      await update.mutateAsync({ id: u.id, body: { status } });
      toast.success(status === 'disabled' ? `${u.fullName} can no longer sign in` : `${u.fullName} can sign in again`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  /** Set sign-in status for every selected person, reporting how many worked. */
  const bulkStatus = async (selected: UserDto[], status: 'active' | 'disabled') => {
    const targets = selected.filter((u) => u.id !== me.user.id && u.status !== status);
    setBusy(true);
    let ok = 0;
    for (const u of targets) {
      try {
        await update.mutateAsync({ id: u.id, body: { status } });
        ok++;
      } catch {
        /* summarised below */
      }
    }
    setBusy(false);
    const label = status === 'disabled' ? 'Sign-in disabled' : 'Sign-in restored';
    if (ok === targets.length) toast.success(`${label} for ${ok} ${ok === 1 ? 'person' : 'people'}`);
    else toast.warning(`${label} for ${ok} of ${targets.length}; the rest failed`);
  };

  const columns: Array<Column<UserDto>> = [
    {
      id: 'name',
      header: 'Name',
      sortValue: (u) => u.fullName,
      searchValue: (u) => `${u.fullName} ${u.email} ${(u.jobs ?? []).map((j) => j.name).join(' ')}`,
      csv: (u) => u.fullName,
      cell: (u) => <Person u={u} you={u.id === me.user.id} onOpen={() => navigate(`/org/users/${u.id}`)} />,
    },
    { id: 'email', header: 'Email', csv: (u) => u.email, className: 'hidden', cell: () => null },
    { id: 'access', header: 'Access', sortValue: (u) => ({ super_user: 'a', pm_admin: 'b', pm_user: 'c' })[levelOf(u)], csv: (u) => LEVEL_LABEL[levelOf(u)], cell: (u) => <AccessLevelBadge level={levelOf(u)} /> },
    { id: 'jobs', header: 'Jobs', csv: (u) => (u.jobs ?? []).map((j) => j.name).join('; '), className: 'hidden max-w-[280px] lg:table-cell', cell: (u) => <JobsSummary user={u} /> },
    { id: 'status', header: 'Status', sortValue: (u) => u.status, cell: (u) => <UserStatusBadge status={u.status} /> },
    { id: 'lastLogin', header: 'Last sign-in', sortValue: (u) => u.lastLoginAt ?? '', csv: (u) => u.lastLoginAt ?? '', cell: (u) => <span className="text-muted-foreground" title={u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : undefined}>{u.lastLoginAt ? fmtRelative(u.lastLoginAt) : 'Never'}</span> },
    { id: 'joined', header: 'Joined', sortValue: (u) => u.createdAt, csv: (u) => u.createdAt, className: 'hidden xl:table-cell', cell: (u) => <span className="text-muted-foreground">{fmtDate(u.createdAt)}</span> },
    {
      id: 'actions',
      header: '',
      noExport: true,
      className: 'w-10',
      cell: (u) => <RowMenu u={u} isSelf={u.id === me.user.id} onEdit={() => navigate(`/org/users/${u.id}`)} onToggle={() => void toggleAccess(u)} />,
    },
  ];

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro
        title="Users"
        description="Everyone who can sign in. Access is set per person: Super Users run the organization; Project Managers work the jobs they are assigned to, as a PM Admin or a PM User on each."
        actions={
          <>
            <Button variant="outline" onClick={() => void api.users.exportXlsx().catch((e: unknown) => toast.error(errorMessage(e)))} title="Download every user with their job assignments as an Excel workbook">
              <DownloadIcon /> Export
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} title="Create, update or disable users and assign jobs from a spreadsheet">
              <UploadIcon /> Import
            </Button>
            {/* The masthead carries Invite on wider screens; phones get it here. */}
            <Button onClick={() => setInviteOpen(true)} className="md:hidden">
              <UserPlusIcon /> Invite user
            </Button>
          </>
        }
      />

      <AccessLevelTiles counts={users.data ? counts : undefined} onPick={pickLevel} active={ACCESS_LEVELS.includes(levelFilter as AccessLevel) ? (levelFilter as AccessLevel) : null} />

      <DataTable
        data={users.data?.items}
        loading={users.isPending}
        error={users.isError ? users.error : undefined}
        onRetry={() => void users.refetch()}
        columns={columns}
        rowId={(u) => u.id}
        searchPlaceholder="Search people, emails, jobs…"
        defaultSort={{ id: 'name', dir: 'asc' }}
        exportName={`${me.tenant.name}-users`}
        highlightId={userId ?? null}
        filters={[
          { key: 'level', label: 'Access', options: ACCESS_LEVELS.map((l) => ({ value: l, label: LEVEL_LABEL[l] })), predicate: (u, v) => levelOf(u) === v, counts: true },
          { key: 'status', label: 'Status', options: [{ value: 'active', label: 'Active' }, { value: 'disabled', label: 'Disabled' }], predicate: (u, v) => u.status === v, counts: true },
        ]}
        selectable
        selectDisabled={(u) => u.id === me.user.id}
        bulkActions={({ selected, clear }) => (
          <>
            <Button size="sm" variant="outline" disabled={busy || selected.every((u) => u.status === 'disabled')} onClick={() => void bulkStatus(selected, 'disabled').then(clear)}>
              Disable sign-in
            </Button>
            <Button size="sm" variant="outline" disabled={busy || selected.every((u) => u.status === 'active')} onClick={() => void bulkStatus(selected, 'active').then(clear)}>
              Restore sign-in
            </Button>
          </>
        )}
        mobileCard={(u) => (
          <div className="grid gap-2">
            <div className="flex items-start justify-between gap-3">
              <Person u={u} you={u.id === me.user.id} onOpen={() => navigate(`/org/users/${u.id}`)} />
              <RowMenu u={u} isSelf={u.id === me.user.id} onEdit={() => navigate(`/org/users/${u.id}`)} onToggle={() => void toggleAccess(u)} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <AccessLevelBadge level={levelOf(u)} />
              {u.status !== 'active' && <UserStatusBadge status={u.status} />}
              <span className="text-muted-foreground text-xs">{u.lastLoginAt ? `Signed in ${fmtRelative(u.lastLoginAt)}` : 'Never signed in'}</span>
            </div>
            <JobsSummary user={u} />
          </div>
        )}
      />

      {invitations.isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        pending.length > 0 && (
          <section className="bg-card border" aria-label="Pending invitations">
            <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
              <h3 className="text-sm font-semibold tracking-tight">Pending invitations</h3>
              <span className="text-muted-foreground text-xs">Waiting to be accepted</span>
            </div>
            <ul className="divide-y border-t">
              {pending.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                  <Avatar className="size-8">
                    <AvatarFallback>
                      <MailIcon className="size-3.5" aria-hidden />
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{i.email}</div>
                    <div className="text-muted-foreground text-xs">
                      Sent {fmtRelative(i.createdAt)} · expires {fmtDate(i.expiresAt)}
                    </div>
                  </div>
                  <div className="hidden max-w-[280px] sm:block">
                    <InvitationJobs invitation={i} />
                  </div>
                  <AccessLevelBadge level={i.level} />
                  <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => void revoke.mutateAsync(i.id).then(() => toast.success('Invitation revoked')).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )
      )}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <UsersImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <EditUserDialog
        user={editing}
        onOpenChange={(o) => {
          if (o) return;
          setEditing(null);
          if (userId) navigate('/org/users', { replace: true });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function Person({ u, you, onOpen }: { u: UserDto; you: boolean; onOpen: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar className="size-8 shrink-0">
        <AvatarFallback className={cn('text-[11px]', u.status !== 'active' && 'text-muted-foreground/60')}>{initials(u.fullName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <button type="button" className="truncate text-left text-sm font-medium hover:underline" onClick={onOpen}>
            {u.fullName}
          </button>
          {you && <span className="text-muted-foreground border px-1 text-[10px] leading-4 font-medium">you</span>}
        </div>
        <div className="text-muted-foreground truncate text-xs">{u.email}</div>
      </div>
    </div>
  );
}

function RowMenu({ u, isSelf, onEdit, onToggle }: { u: UserDto; isSelf: boolean; onEdit: () => void; onToggle: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${u.fullName}`} onClick={(e) => e.stopPropagation()}>
          <MoreHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>Edit access and jobs</DropdownMenuItem>
        {!isSelf && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onToggle} variant={u.status === 'active' ? 'destructive' : undefined}>
              {u.status === 'active' ? 'Disable sign-in' : 'Restore sign-in'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function JobsSummary({ user }: { user: UserDto }) {
  if (user.tenantRole === 'org_admin') return <span className="text-muted-foreground text-xs">Every job</span>;
  const jobs = user.jobs ?? [];
  if (jobs.length === 0) return <span className="text-warning text-xs">No jobs assigned</span>;
  const shown = jobs.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1 text-xs">
      {shown.map((j) => (
        <span key={j.id} className="bg-muted inline-flex items-center gap-1 px-1.5 py-0.5" title={`${LEVEL_LABEL[j.role === 'admin' ? 'pm_admin' : 'pm_user']} on ${j.name}`}>
          {j.name}
          <span className="text-muted-foreground">· {j.role === 'admin' ? 'Admin' : 'User'}</span>
        </span>
      ))}
      {jobs.length > shown.length && <span className="text-muted-foreground px-1 py-0.5">+{jobs.length - shown.length} more</span>}
    </div>
  );
}

function InvitationJobs({ invitation }: { invitation: InvitationDto }) {
  if (invitation.tenantRole === 'org_admin') return <span className="text-muted-foreground text-xs">Every job</span>;
  if (invitation.jobs.length === 0) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1 text-xs">
      {invitation.jobs.map((j) => (
        <span key={j.id} className="bg-muted inline-flex items-center gap-1 px-1.5 py-0.5">
          {j.name}
          <span className="text-muted-foreground">· {j.role === 'admin' ? 'Admin' : 'User'}</span>
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

const inviteSchema = z
  .object({
    email: z.email('Enter a valid email address'),
    level: z.enum(['super_user', 'pm_admin', 'pm_user']),
    jobs: z.array(z.object({ jobId: z.string(), role: z.enum(['admin', 'user']) })),
    message: z.string().trim().max(500),
  })
  .refine((v) => v.level === 'super_user' || v.jobs.length > 0, { message: 'Pick at least one job for a Project Manager', path: ['jobs'] });
type InviteValues = z.infer<typeof inviteSchema>;

function InviteUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const me = useMe();
  const invite = useInviteUser();
  const [error, setError] = useState<string | null>(null);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);
  const form = useForm<InviteValues>({ resolver: zodResolver(inviteSchema), defaultValues: { email: '', level: 'pm_user', jobs: [], message: '' } });
  const level = form.watch('level');
  const activeJobs = me.jobs.filter((j) => j.status === 'active');

  useEffect(() => {
    if (!open) {
      form.reset();
      setError(null);
      setAcceptUrl(null);
    }
  }, [open, form]);

  // Switching between PM Admin and PM User re-roles every picked job; Super Users need no jobs.
  const changeLevel = (next: AccessLevel) => {
    form.setValue('level', next, { shouldValidate: true });
    if (next === 'super_user') form.setValue('jobs', []);
    else form.setValue('jobs', form.getValues('jobs').map((j) => ({ ...j, role: jobRoleForLevel(next) })));
  };

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await invite.mutateAsync({ email: v.email, tenantRole: v.level === 'super_user' ? 'org_admin' : 'member', jobs: v.level === 'super_user' ? [] : v.jobs, message: v.message || undefined });
      if (res.existingUser) {
        toast.success(`${v.email} already has an account and was assigned to the selected jobs`);
        onOpenChange(false);
        return;
      }
      toast.success(`Invitation sent to ${v.email}`);
      if (res.acceptUrl) setAcceptUrl(res.acceptUrl);
      else onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
          <DialogDescription>They receive an email with a sign-up link. Choose what they can do; Project Managers also need the jobs they work on.</DialogDescription>
        </DialogHeader>
        {acceptUrl ? (
          <div className="grid gap-4">
            <Alert>
              <MailIcon />
              <AlertTitle>Invitation created</AlertTitle>
              <AlertDescription>
                <p>Email delivery is simulated in this environment. Share this link directly:</p>
                <code className="bg-muted mt-1 block w-full break-all p-2 text-xs">{acceptUrl}</code>
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <CopyButton value={acceptUrl} label="Copy link" />
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-5" noValidate>
            <FormError message={error} />
            <Field label="Email" htmlFor="inv-email" required error={form.formState.errors.email?.message}>
              <Input id="inv-email" type="email" autoFocus {...form.register('email')} />
            </Field>
            <div className="grid gap-2">
              <span className="text-sm font-medium">Access level</span>
              <AccessLevelCards options={LEVEL_OPTIONS} value={level} onChange={changeLevel} />
            </div>
            {level !== 'super_user' && (
              <Controller
                control={form.control}
                name="jobs"
                render={({ field, fieldState }) => (
                  <div className="grid gap-2">
                    <span className="text-sm font-medium">Jobs they work on</span>
                    <JobAssignmentPicker jobs={activeJobs} value={field.value as JobAssignment[]} onChange={field.onChange} defaultRole={jobRoleForLevel(level)} />
                    <p className="text-muted-foreground text-xs">The level applies to every checked job; change it per job if someone sends on one job but not another.</p>
                    {fieldState.error && <p className="text-destructive text-xs">{fieldState.error.message}</p>}
                  </div>
                )}
              />
            )}
            <Field label="Personal note" htmlFor="inv-msg" error={form.formState.errors.message?.message}>
              <Textarea id="inv-msg" rows={2} placeholder="Optional" {...form.register('message')} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? 'Sending…' : `Invite as ${LEVEL_LABEL[level]}`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onOpenChange }: { user: UserDto | null; onOpenChange: (o: boolean) => void }) {
  const me = useMe();
  const update = useUpdateUser();
  const setJobs = useSetUserJobs();
  const detail = useUserDetail(user?.id ?? null);
  const [kind, setKind] = useState<'super_user' | 'pm'>('pm');
  const [jobs, setJobsState] = useState<JobAssignment[]>([]);
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [error, setError] = useState<string | null>(null);
  const activeJobs = me.jobs.filter((j) => j.status === 'active');

  useEffect(() => {
    if (user) {
      setKind(user.tenantRole === 'org_admin' ? 'super_user' : 'pm');
      setStatus(user.status);
      setError(null);
    }
  }, [user]);
  useEffect(() => {
    if (detail.data) setJobsState(detail.data.jobs.filter((j) => j.role).map((j) => ({ jobId: j.id, role: j.role as 'admin' | 'user' })));
  }, [detail.data]);

  const isSelf = user?.id === me.user.id;
  const originalJobs = (detail.data?.jobs ?? []).filter((j) => j.role).map((j) => `${j.id}:${j.role}`).sort().join('|');
  const jobsDirty = jobs.map((j) => `${j.jobId}:${j.role}`).sort().join('|') !== originalJobs;

  const save = async () => {
    if (!user) return;
    setError(null);
    if (kind === 'pm' && jobs.length === 0) {
      setError('A Project Manager needs at least one job. Make them a Super User instead if they should see everything.');
      return;
    }
    try {
      const tenantRole = kind === 'super_user' ? 'org_admin' : 'member';
      if (tenantRole !== user.tenantRole || status !== user.status) await update.mutateAsync({ id: user.id, body: { tenantRole, status } });
      if (kind === 'pm' && jobsDirty) await setJobs.mutateAsync({ id: user.id, jobs });
      toast.success('Access updated');
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const busy = update.isPending || setJobs.isPending;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{user?.fullName}</DialogTitle>
          <DialogDescription>{user?.email}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          <FormError message={error} />
          <div className="grid gap-2">
            <span className="text-sm font-medium">Access level</span>
            <AccessLevelCards
              name="edit-level"
              disabled={isSelf}
              value={kind}
              onChange={setKind}
              options={[
                { value: 'super_user', label: LEVEL_LABEL.super_user, hint: LEVEL_DESCRIPTION.super_user },
                { value: 'pm', label: 'Project Manager', hint: 'Works only the jobs below, as a PM Admin (can send texts) or PM User (cannot) on each one.' },
              ]}
            />
          </div>
          {kind === 'pm' && (
            <div className="grid gap-2">
              <span className="text-sm font-medium">Jobs and level on each</span>
              {detail.isPending ? <p className="text-muted-foreground text-xs">Loading…</p> : <JobAssignmentPicker jobs={activeJobs} value={jobs} onChange={setJobsState} defaultRole="user" disabled={isSelf} />}
            </div>
          )}
          <Field label="Sign-in" htmlFor="edit-status" description={status === 'disabled' ? 'Disabled users are signed out immediately and cannot sign in.' : undefined}>
            <Select value={status} onValueChange={(v) => setStatus(v as 'active' | 'disabled')} disabled={isSelf}>
              <SelectTrigger id="edit-status" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {isSelf && <p className="text-muted-foreground text-xs">You cannot change your own access.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy || isSelf}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
